/**
 * Authenticated fetch wrapper for the DVMS backend API.
 * Reads API_DOMAIN from runtime config and attaches the Okta access token.
 * - Enforces a 30s request timeout via AbortSignal.
 * - Retries once on 401 by renewing the Okta access token.
 */
import { getConfig } from '../config/loader';
import { getOktaAuth } from '../auth/okta';
import { incrementRequests, decrementRequests } from './requestCounter';

const REQUEST_TIMEOUT_MS = 30_000;

/**
 * A non-OK API response. `message` is the server's `detail` (or a generic
 * fallback), exactly as the plain Error used to carry it; `status` and the
 * RFC 9457 `code` let callers tell a 409 duplicate from any other failure.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly code: string | undefined;

    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
    }
}

/** True for a 409 Conflict or an ALREADY_EXISTS problem code. */
export function isConflictError(err: unknown): boolean {
    return err instanceof ApiError && (err.status === 409 || err.code === 'ALREADY_EXISTS');
}

/** Builds the ApiError for a non-OK response from its (possibly non-JSON) body. */
function apiErrorFrom(status: number, body: string): ApiError {
    let message = `API error ${status}`;
    let code: string | undefined;
    try {
        const parsed = JSON.parse(body) as { detail?: string; message?: string; error?: string; code?: string };
        message = parsed.detail ?? parsed.message ?? parsed.error ?? message;
        code = parsed.code;
    } catch {
        // non-JSON error body
    }
    return new ApiError(message, status, code);
}

async function doFetch<T>(
    path: string,
    options: RequestInit,
    isRetry: boolean,
    timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<T> {
    const config = getConfig();
    const url = `${config.API_DOMAIN}${path}`;

    let token: string | undefined;
    try {
        token = getOktaAuth().getAccessToken();
    } catch {
        // Okta not initialized (dev mode) — proceed without token
    }

    const headers: Record<string, string> = {
        Accept: 'application/json',
        ...(options.headers as Record<string, string>),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    // The timeout owns its own controller, but a caller-supplied signal has to be
    // relayed into it — passing `options.signal` straight through to fetch would
    // be overwritten by the timeout signal below, which is why caller aborts were
    // silently ignored before. `timedOut` keeps the two causes distinguishable so
    // a deliberate caller abort isn't reported to the user as a timeout.
    // Held in an object rather than a plain `let` so TypeScript doesn't narrow it
    // to `false` at the catch site — it's only ever assigned inside the callback.
    const controller = new AbortController();
    const timeout = { fired: false };
    const timeoutId = setTimeout(() => {
        timeout.fired = true;
        controller.abort();
    }, timeoutMs);

    const callerSignal = options.signal;
    const relayAbort = () => {
        controller.abort();
    };
    if (callerSignal) {
        if (callerSignal.aborted) {
            controller.abort();
        } else {
            callerSignal.addEventListener('abort', relayAbort, { once: true });
        }
    }

    let resp: Response;
    try {
        resp = await fetch(url, { ...options, headers, signal: controller.signal });
    } catch (err) {
        if ((err as Error).name === 'AbortError') {
            if (timeout.fired) {
                throw new Error('Request timed out. Please try again.');
            }
            // Propagate as AbortError so callers can tell a superseded request
            // apart from a genuine failure and swallow it.
            throw err;
        }
        throw err;
    } finally {
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener('abort', relayAbort);
    }

    // Retry once on 401 by renewing the access token
    if (resp.status === 401 && !isRetry) {
        try {
            await getOktaAuth().tokenManager.renew('accessToken');
        } catch {
            throw new Error('Session expired. Please sign in again.');
        }
        return doFetch<T>(path, options, true, timeoutMs);
    }

    if (!resp.ok) {
        throw apiErrorFrom(resp.status, await resp.text());
    }

    // 204 No Content
    if (resp.status === 204) {
        return undefined as T;
    }

    return resp.json() as Promise<T>;
}

export async function apiFetch<T = unknown>(
    path: string,
    options: RequestInit = {},
    timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<T> {
    incrementRequests();
    try {
        return await doFetch<T>(path, options, false, timeoutMs);
    } finally {
        decrementRequests();
    }
}

/**
 * Multipart form-data fetch with the same features as apiFetch:
 * curl logging, Okta auth, 30s timeout, 401 retry, request counter.
 * Does NOT set Content-Type — browser sets it with the boundary automatically.
 */
export async function apiFormFetch<T>(path: string, formData: FormData, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T> {
    const config = getConfig();
    const url = `${config.API_DOMAIN}${path}`;

    let token: string | undefined;
    try {
        token = getOktaAuth().getAccessToken();
    } catch {
        // Okta not initialized
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeoutMs);

    incrementRequests();
    try {
        let resp: Response;
        try {
            resp = await fetch(url, { method: 'POST', headers, body: formData, signal: controller.signal });
        } catch (err) {
            if ((err as Error).name === 'AbortError') {
                throw new Error('Request timed out. Please try again.');
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }

        // Retry once on 401
        if (resp.status === 401) {
            try {
                await getOktaAuth().tokenManager.renew('accessToken');
                const newToken = getOktaAuth().getAccessToken();
                if (newToken) {
                    headers['Authorization'] = `Bearer ${newToken}`;
                }
            } catch {
                throw new Error('Session expired. Please sign in again.');
            }
            const retryController = new AbortController();
            const retryTimeout = setTimeout(() => {
                retryController.abort();
            }, REQUEST_TIMEOUT_MS);
            try {
                resp = await fetch(url, { method: 'POST', headers, body: formData, signal: retryController.signal });
            } finally {
                clearTimeout(retryTimeout);
            }
        }

        if (!resp.ok) {
            throw apiErrorFrom(resp.status, await resp.text());
        }

        if (resp.status === 204) {
            return undefined as T;
        }

        return await (resp.json() as Promise<T>);
    } finally {
        decrementRequests();
    }
}
